import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../common/form/CustomTypography";
import CustomTextField from "../common/textFields/TextField";
import AppButton from "../common/buttons/AppButton";
import ImageUpload from "../common/upload/ImageUpload";
import ImageAPI from "../../../api/ImageAPI";
import { CircularProgress } from "@mui/material";

const imageAPI = new ImageAPI();

interface UploadClientPOPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (poNumber: string, poFileUrl: string) => void;
}

const UploadClientPOPopup: React.FC<UploadClientPOPopupProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const [poNumber, setPONumber] = useState<string>("");
  const [poFile, setPOFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    poNumber?: string;
    poFile?: string;
    upload?: string;
  }>({});

  useEffect(() => {
    if (open) {
      setPONumber("");
      setPOFile(null);
      setErrors({});
      setIsUploading(false);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: { poNumber?: string; poFile?: string } = {};

    if (!poNumber || poNumber.trim() === "") {
      newErrors.poNumber = "PO Number is required";
    }

    if (!poFile) {
      newErrors.poFile = "PO document is required";
    } else {
      // Validate file type
      if (poFile.type !== "application/pdf") {
        newErrors.poFile = "Only PDF files are allowed";
      }
      // Validate file size (max 10MB)
      if (poFile.size > 5 * 1024 * 1024) {
        newErrors.poFile = "File size must be less than 5MB";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Clear previous upload errors
    setErrors((prev) => ({ ...prev, upload: undefined }));

    if (!validateForm()) {
      return;
    }

    // Only upload file if PO number is present
    if (!poNumber || poNumber.trim() === "") {
      setErrors({ poNumber: "PO Number is required" });
      return;
    }

    setIsUploading(true);

    try {
      const poFileUrl = await imageAPI.uploadFile(poFile, "client-po");

      // Upload file only if PO number exists and file is present
      if (!poFileUrl) {
        throw new Error("Failed to upload file");
      }

      // Pass PO number and URL back to parent
      onSave(poNumber.trim(), poFileUrl);
      // Close popup after successful save
      onClose();
    } catch (error: any) {
      setErrors((prev) => ({
        ...prev,
        upload:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload PO document. Please try again.",
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (files: File[]) => {
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          poFile: "Only PDF files are allowed",
        }));
        return;
      }
      // Validate file size
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          poFile: "File size must be less than 5MB",
        }));
        return;
      }
      setPOFile(file);
      setErrors((prev) => ({ ...prev, poFile: undefined }));
    }
  };

  const handleRemoveFile = (image?: any) => {
    setPOFile(null);
    setErrors((prev) => ({ ...prev, poFile: undefined }));
  };

  const handlePONumberChange = (value: string) => {
    setPONumber(value);
    if (errors.poNumber && value.trim() !== "") {
      setErrors((prev) => ({ ...prev, poNumber: undefined }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${
              theme.palette.customColors?.white ||
              theme.palette.background.paper
            } 0%, ${
              theme.palette.customColors?.lightGreyColor ||
              theme.palette.grey[50]
            } 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiDialog-paper": {
          zIndex: 2000,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 0%, ${
              theme.palette.customColors?.blueGradientEnd ||
              theme.palette.primary.dark
            } 50%, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main + "15",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomTypography
                variant="body1"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: "20px",
                }}
              >
                📄
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                variant="heading4"
                fontWeight={600}
                color={
                  theme.palette.customColors?.blackBorder || "text.primary"
                }
              >
                Upload Client PO
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={
                  theme.palette.customColors?.greyTextColor || "text.secondary"
                }
                sx={{ mt: 0.5 }}
              >
                Enter PO number and upload the PO document
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color:
                theme.palette.customColors?.greyTextColor ||
                theme.palette.text.secondary,
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greyBackground ||
                  theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                theme.palette.customColors?.greyBackground ||
                theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.customColors?.greyDarkborder ||
                theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.grey[600],
              },
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* PO Number Input */}
            <Box>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors?.greyTextColor}
                style={{ marginBottom: 16 }}
              >
                PO Number <span style={{ color: "red" }}>*</span>
              </CustomTypography>
              <CustomTextField
                width="100%"
                value={poNumber}
                placeholder="Enter PO number"
                onSearch={handlePONumberChange}
                error={!!errors.poNumber}
              />
              {errors.poNumber && (
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    mt: 0.5,
                    ml: 1,
                  }}
                >
                  {errors.poNumber}
                </CustomTypography>
              )}
            </Box>

            {/* PO File Upload */}
            <Box>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors?.greyTextColor}
              >
                PO Document (PDF) <span style={{ color: "red" }}>*</span>
              </CustomTypography>
              <ImageUpload
                size="large"
                showImagesList={false}
                uploadText="Drop PDF file here or click to browse"
                addImagesToBeUploaded={handleFileUpload}
                removeImage={handleRemoveFile}
                allowedFileTypes=".pdf"
                fileInfo="Maximum file size: 5MB"
                disabled={false}
              />
              {errors.poFile && (
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    mt: 0.5,
                    ml: 1,
                  }}
                >
                  {errors.poFile}
                </CustomTypography>
              )}
              {poFile && !errors.poFile && (
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: theme.palette.success.main,
                    mt: 0.5,
                    ml: 1,
                  }}
                >
                  ✓ {poFile.name} ({(poFile.size / 1024 / 1024).toFixed(2)} MB)
                </CustomTypography>
              )}
            </Box>

            {/* Upload Error Display */}
            {errors.upload && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: theme.palette.error.main + "10",
                  border: `1px solid ${theme.palette.error.main}30`,
                }}
              >
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span>⚠️</span>
                  {errors.upload}
                </CustomTypography>
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            padding: { xs: "12px 16px", sm: "16px 20px" },
            borderTop: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
            backgroundColor:
              theme.palette.customColors?.greyBackground ||
              theme.palette.grey[50],
          }}
        >
          <AppButton
            onClick={onClose}
            variant="primary"
            disabled={isUploading}
            customStyles={{
              borderColor:
                theme.palette.customColors?.greyDarkborder ||
                theme.palette.grey[400],
              color:
                theme.palette.customColors?.white || theme.palette.common.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                borderColor:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.text.primary,
                backgroundColor:
                  theme.palette.customColors?.greyDarkborder ||
                  theme.palette.grey[100],
              },
            }}
            label="Cancel"
          />

          <AppButton
            onClick={handleSave}
            variant="primary"
            disabled={isUploading}
            customStyles={{
              backgroundColor:
                theme.palette.customColors?.EmeraldGreen ||
                theme.palette.success.main,
              color:
                theme.palette.customColors?.white || theme.palette.common.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greenDark ||
                  theme.palette.success.dark,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px ${
                  theme.palette.customColors?.EmeraldGreen ||
                  theme.palette.success.main
                }30`,
              },
              "&:disabled": {
                backgroundColor:
                  theme.palette.customColors?.greyDarkborder ||
                  theme.palette.grey[400],
                color: theme.palette.common.white,
              },
            }}
            label={
              isUploading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  Uploading...
                </Box>
              ) : (
                "Save"
              )
            }
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default UploadClientPOPopup;
